import React from "react";
import { Link } from "react-router-dom";
import { Check, ArrowRight, Info } from "lucide-react";
import { PageHero } from "@/components/Decor";
import {
  TARIFS_SOUTIEN,
  TARIFS_TRANSPORT,
  TARIFS_MATIN,
  TARIF_MATIN_UNITAIRE,
  TARIF_DEMI_JOURNEE,
  TARIF_TRAJET,
  TARIF_STAGE,
  formatXPF,
} from "@/lib/data";

function PriceTable({ title, eyebrow, rows, footnote, highlight }) {
  return (
    <div className={`rounded-3xl p-7 sm:p-8 border ${highlight ? "bg-ocean text-white border-ocean" : "bg-white border-ocean/5"}`}>
      <div className={`text-xs uppercase tracking-[0.25em] mb-3 font-semibold ${highlight ? "text-coral" : "text-coral"}`}>{eyebrow}</div>
      <h3 className={`font-display text-2xl mb-6 ${highlight ? "text-white" : "text-ocean"}`}>{title}</h3>
      <div className="space-y-2.5">
        {rows.map((r) => (
          <div key={r.label} className={`flex items-center justify-between py-2.5 ${highlight ? "border-b border-white/10 last:border-0" : "border-b border-ocean/5 last:border-0"}`}>
            <span className={highlight ? "text-white/75" : "text-ocean/70"}>{r.label}</span>
            <div className="text-right">
              <div className={`font-display text-xl ${highlight ? "text-white" : "text-ocean"}`}>{r.price}</div>
              {r.sub && (
                <div className={`text-[10px] uppercase tracking-wider mt-0.5 ${highlight ? "text-white/45" : "text-ocean/45"}`}>{r.sub}</div>
              )}
            </div>
          </div>
        ))}
      </div>
      {footnote && (
        <div className={`mt-5 flex items-start gap-2 text-xs ${highlight ? "text-white/55" : "text-ocean/55"}`}>
          <Info size={14} className="mt-0.5 shrink-0" />
          <span>{footnote}</span>
        </div>
      )}
    </div>
  );
}

export default function TarifsPage() {
  return (
    <>
      <PageHero
        eyebrow="Tarifs"
        title={<>Des formules <em className="italic text-coral">claires et accessibles</em></>}
        subtitle="Tous nos tarifs en Franc Pacifique (XPF). Paiement par virement bancaire — coordonnées transmises après inscription."
      />

      <section className="relative pb-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-6">
            <PriceTable
              eyebrow="Service phare"
              title="Soutien scolaire (mensuel)"
              highlight
              rows={[
                { label: "Demi-journée (unitaire)", price: formatXPF(TARIF_DEMI_JOURNEE) },
                ...TARIFS_SOUTIEN.map((t) => ({ label: t.freq, price: formatXPF(t.prix), sub: `soit ${formatXPF(t.per_half)} / demi-journée` })),
              ]}
              footnote="Goûter inclus dans le forfait mensuel. Du lundi au vendredi, hors jours fériés."
            />
            <PriceTable
              eyebrow="Ramassage scolaire"
              title="Transport (mensuel)"
              rows={[
                { label: "Le trajet (unitaire)", price: formatXPF(TARIF_TRAJET) },
                ...TARIFS_TRANSPORT.map((t) => ({ label: t.freq, price: formatXPF(t.prix), sub: `soit ${formatXPF(t.per_trip)} / trajet` })),
              ]}
              footnote="Forfait mensuel toutes écoles — sur la base de 3 semaines/mois."
            />
          </div>

          <div className="mt-6 grid lg:grid-cols-2 gap-6">
            <PriceTable
              eyebrow="Nouveau — dès 4h"
              title="Accueil du matin"
              rows={[
                { label: "Le jour (unitaire)", price: formatXPF(TARIF_MATIN_UNITAIRE) },
                ...TARIFS_MATIN.map((t) => ({ label: t.freq, price: formatXPF(t.prix), sub: `soit ${formatXPF(t.per_day)} / jour` })),
              ]}
              footnote="Petit-déjeuner et dépose à l'école inclus. Base de calcul : 3 semaines d'école par mois."
            />
            <PriceTable
              eyebrow="Vacances scolaires"
              title="Stages de vacances"
              rows={[
                { label: "Journée — non-abonné", price: formatXPF(TARIF_STAGE.non_abonne_jour) },
                { label: "Semaine — non-abonné", price: formatXPF(TARIF_STAGE.non_abonne_semaine) },
                { label: "Journée — abonné soutien", price: formatXPF(TARIF_STAGE.abonne_jour) },
                { label: "Semaine — abonné soutien", price: formatXPF(TARIF_STAGE.abonne_semaine) },
              ]}
              footnote="Goûters et déjeuners inclus."
            />
          </div>

          {/* Inclus */}
          <div className="mt-10 bg-coral/10 border border-coral/20 rounded-3xl p-7 sm:p-10">
            <h3 className="font-display text-2xl text-ocean mb-5">Toujours inclus dans nos formules</h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
              {[
                "Encadrement professionnel",
                "Environnement sécurisé",
                "Activités d'éveil",
                "Suivi régulier",
              ].map((x) => (
                <div key={x} className="flex items-center gap-2 text-ocean/80">
                  <Check size={18} className="text-coral" />
                  {x}
                </div>
              ))}
            </div>
          </div>

          <div className="mt-12 text-center">
            <Link to="/inscription" className="btn-pill btn-accent inline-flex" data-testid="tarifs-cta-inscription">
              Inscrire mon enfant <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
